// ============================================================
// FILE 6: frontend/src/pages/SmartStudy/SmartStudy.js
// Smart Study page — SM-2 Spaced Repetition UI
// Gated behind FeatureGate for Pro users
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import {
  Box, Typography, Button, Card, CardContent, Paper,
  LinearProgress, Chip, IconButton, Select, MenuItem,
  FormControl, InputLabel, TextField, Alert, Skeleton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Tabs, Tab, Tooltip, Divider
} from '@mui/material';
import {
  Brain, RotateCcw, Check, X, Lightbulb, ChevronRight,
  BarChart3, BookOpen, Clock, Zap, Trophy, RefreshCw,
  ArrowRight, AlertCircle
} from 'lucide-react';
import FeatureGate from '../../components/subscription/FeatureGate';
import UpgradePrompt from '../../components/subscription/UpgradePrompt';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// KaTeX rendering helper — reuse existing platform pattern
const renderLatex = (text) => {
  if (!text) return '';
  // If KaTeX is available globally (as in existing QuestionDisplay)
  if (typeof window !== 'undefined' && window.katex) {
    try {
      return text.replace(/\$\$(.*?)\$\$/g, (_, tex) =>
        window.katex.renderToString(tex, { displayMode: true, throwOnError: false })
      ).replace(/\$(.*?)\$/g, (_, tex) =>
        window.katex.renderToString(tex, { displayMode: false, throwOnError: false })
      );
    } catch (e) { return text; }
  }
  return text;
};

// ---- Sub-components ----

function StatsOverview({ stats, loading }) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        {[1, 2, 3, 4].map(i => (
          <Skeleton key={i} variant="rounded" width={140} height={90} />
        ))}
      </Box>
    );
  }
  if (!stats) return null;

  const cards = [
    { label: 'Due Today', value: stats.dueToday, icon: Clock, color: '#f44336' },
    { label: 'Learning', value: stats.statusCounts?.learning || 0, icon: BookOpen, color: '#ff9800' },
    { label: 'Reviewing', value: stats.statusCounts?.review || 0, icon: RefreshCw, color: '#2196f3' },
    { label: 'Mastered', value: stats.statusCounts?.mastered || 0, icon: Trophy, color: '#4caf50' }
  ];

  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
      {cards.map(c => (
        <Paper
          key={c.label}
          elevation={0}
          sx={{
            p: 2, flex: '1 1 130px', minWidth: 130, maxWidth: 180,
            border: '1px solid', borderColor: 'divider', borderRadius: 2,
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5
          }}
        >
          <c.icon size={20} color={c.color} />
          <Typography variant="h5" fontWeight={700} sx={{ color: c.color }}>
            {c.value}
          </Typography>
          <Typography variant="caption" color="text.secondary">{c.label}</Typography>
        </Paper>
      ))}
    </Box>
  );
}

function TopicProgress({ topics }) {
  if (!topics || topics.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        Start studying to see your topic progress here.
      </Typography>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        Topic Breakdown
      </Typography>
      {topics.map(t => {
        const masteredPct = t.total > 0 ? Math.round((t.mastered / t.total) * 100) : 0;
        return (
          <Box key={t._id} sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">{t._id}</Typography>
              <Typography variant="caption" color="text.secondary">
                {t.mastered}/{t.total} mastered
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={masteredPct}
              sx={{
                height: 6, borderRadius: 3,
                bgcolor: 'action.hover',
                '& .MuiLinearProgress-bar': {
                  bgcolor: masteredPct >= 75 ? '#4caf50' : masteredPct >= 40 ? '#ff9800' : '#f44336',
                  borderRadius: 3
                }
              }}
            />
          </Box>
        );
      })}
    </Box>
  );
}

function QuestionCard({
  question, onAnswer, onSkip, showingAnswer, answerResult,
  currentIndex, totalCount
}) {
  const [userAnswer, setUserAnswer] = useState('');
  const [selectedOption, setSelectedOption] = useState(null);
  const [hintsShown, setHintsShown] = useState(0);
  const [startTime] = useState(Date.now());

  const isMultipleChoice = question.options && question.options.length > 0;

  const handleSubmit = () => {
    const answer = isMultipleChoice ? selectedOption : userAnswer.trim();
    if (!answer) return;

    const timeSpent = Math.round((Date.now() - startTime) / 1000);
    onAnswer(answer, hintsShown, timeSpent);
  };

  const handleShowHint = () => {
    if (question.hints && hintsShown < question.hints.length) {
      setHintsShown(prev => prev + 1);
    }
  };

  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid', borderColor: 'divider', borderRadius: 3,
        position: 'relative', overflow: 'visible'
      }}
    >
      {/* Progress bar */}
      <LinearProgress
        variant="determinate"
        value={totalCount > 0 ? ((currentIndex + 1) / totalCount) * 100 : 0}
        sx={{ borderRadius: '12px 12px 0 0', height: 4 }}
      />

      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label={question.topic}
              size="small"
              sx={{ bgcolor: 'primary.50', color: 'primary.main' }}
            />
            <Chip
              label={question.difficulty || 'medium'}
              size="small"
              color={
                question.difficulty === 'easy' ? 'success'
                : question.difficulty === 'hard' ? 'error'
                : 'warning'
              }
              variant="outlined"
            />
            {question.reviewState && (
              <Chip
                label={question.source === 'due_review' ? 'Review' : 'New'}
                size="small"
                variant="outlined"
                icon={question.source === 'due_review' ? <RefreshCw size={12} /> : <Zap size={12} />}
              />
            )}
          </Box>
          <Typography variant="caption" color="text.secondary">
            {currentIndex + 1} / {totalCount}
          </Typography>
        </Box>

        {/* Question text */}
        <Typography
          variant="body1"
          sx={{ mb: 3, lineHeight: 1.8, fontSize: '1.05rem' }}
          dangerouslySetInnerHTML={{ __html: renderLatex(question.questionText) }}
        />

        {/* Image/diagram */}
        {question.image && (
          <Box sx={{ mb: 2, textAlign: 'center' }}>
            {/* MathDiagram renderer would go here — pass question.image */}
          </Box>
        )}

        {/* Hints */}
        {question.hints && question.hints.length > 0 && !showingAnswer && (
          <Box sx={{ mb: 2 }}>
            {Array.from({ length: hintsShown }).map((_, i) => (
              <Alert key={i} severity="info" icon={<Lightbulb size={18} />} sx={{ mb: 1 }}>
                <Typography
                  variant="body2"
                  dangerouslySetInnerHTML={{
                    __html: renderLatex(
                      typeof question.hints[i] === 'object'
                        ? question.hints[i].text
                        : question.hints[i]
                    )
                  }}
                />
              </Alert>
            ))}
            {hintsShown < question.hints.length && (
              <Button
                size="small"
                startIcon={<Lightbulb size={14} />}
                onClick={handleShowHint}
                sx={{ textTransform: 'none' }}
              >
                Show hint ({hintsShown}/{question.hints.length})
              </Button>
            )}
          </Box>
        )}

        {/* Answer area */}
        {!showingAnswer ? (
          <Box>
            {isMultipleChoice ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                {question.options.map(opt => (
                  <Paper
                    key={opt.label}
                    elevation={0}
                    onClick={() => setSelectedOption(opt.label)}
                    sx={{
                      p: 2, cursor: 'pointer', border: '2px solid',
                      borderColor: selectedOption === opt.label ? 'primary.main' : 'divider',
                      borderRadius: 2, transition: 'all 0.15s',
                      bgcolor: selectedOption === opt.label ? 'primary.50' : 'transparent',
                      '&:hover': { borderColor: 'primary.light' }
                    }}
                  >
                    <Typography variant="body2">
                      <strong>{opt.label}.</strong>{' '}
                      <span dangerouslySetInnerHTML={{ __html: renderLatex(opt.text) }} />
                    </Typography>
                  </Paper>
                ))}
              </Box>
            ) : (
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Type your answer..."
                value={userAnswer}
                onChange={e => setUserAnswer(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }}
                sx={{ mb: 2 }}
                autoFocus
              />
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
              <Button
                variant="text"
                color="inherit"
                onClick={onSkip}
                sx={{ textTransform: 'none' }}
              >
                Skip
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={isMultipleChoice ? !selectedOption : !userAnswer.trim()}
                endIcon={<ArrowRight size={16} />}
                sx={{ textTransform: 'none', px: 4 }}
              >
                Check Answer
              </Button>
            </Box>
          </Box>
        ) : (
          /* Post-answer feedback */
          <Box>
            <Alert
              severity={answerResult.isCorrect ? 'success' : 'error'}
              icon={answerResult.isCorrect ? <Check size={20} /> : <X size={20} />}
              sx={{ mb: 2, borderRadius: 2 }}
            >
              <Typography variant="subtitle2" fontWeight={600}>
                {answerResult.isCorrect ? 'Correct!' : 'Not quite'}
              </Typography>
              <Typography variant="body2">
                Correct answer: <strong>{answerResult.correctAnswer}</strong>
              </Typography>
            </Alert>

            {/* SM-2 feedback */}
            <Box
              sx={{
                display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2,
                p: 2, bgcolor: 'action.hover', borderRadius: 2
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">Next review</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {answerResult.nextReviewIn}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">Quality</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {answerResult.quality}/5
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Chip
                  label={answerResult.reviewState?.status}
                  size="small"
                  color={
                    answerResult.reviewState?.status === 'mastered' ? 'success'
                    : answerResult.reviewState?.status === 'review' ? 'info'
                    : 'warning'
                  }
                />
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">Streak</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {answerResult.reviewState?.correctStreak || 0}
                </Typography>
              </Box>
            </Box>

            {/* Explanation */}
            {answerResult.explanation && (
              <Paper
                elevation={0}
                sx={{ p: 2, bgcolor: 'info.50', border: '1px solid', borderColor: 'info.light', borderRadius: 2, mb: 2 }}
              >
                <Typography variant="subtitle2" gutterBottom>Explanation</Typography>
                <Typography
                  variant="body2"
                  dangerouslySetInnerHTML={{ __html: renderLatex(answerResult.explanation) }}
                />
              </Paper>
            )}

            <Button
              variant="contained"
              fullWidth
              onClick={() => onAnswer(null, 0, 0, true)} // Signal: next question
              endIcon={<ChevronRight size={16} />}
              sx={{ textTransform: 'none', mt: 1 }}
            >
              Next Question
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

// ---- Main Component ----

function SmartStudyContent() {
  const { user, token } = useAuth();
  const [tab, setTab] = useState(0); // 0 = Study, 1 = Stats
  const [grade, setGrade] = useState(user?.profile?.grade || 8);
  const [topic, setTopic] = useState('');
  const [topics, setTopics] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [showingAnswer, setShowingAnswer] = useState(false);
  const [answerResult, setAnswerResult] = useState(null);
  const [sessionStats, setSessionStats] = useState({ answered: 0, correct: 0 });
  const [resetDialog, setResetDialog] = useState(false);
  const [error, setError] = useState(null);

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  // Fetch available topics for the selected grade
  const fetchTopics = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/smart-study/topics?grade=${grade}`, { headers });
      const data = await res.json();
      if (data.success) setTopics(data.data);
    } catch (e) { console.error('Failed to fetch topics:', e); }
  }, [grade, token]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await fetch(`${API_URL}/smart-study/stats?grade=${grade}`, { headers });
      const data = await res.json();
      if (data.success) setStats(data.data);
    } catch (e) { console.error('Failed to fetch stats:', e); }
    setStatsLoading(false);
  }, [grade, token]);

  // Fetch next batch of questions
  const fetchQuestions = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = `${API_URL}/smart-study/next?grade=${grade}&limit=10${topic ? '&topic=' + encodeURIComponent(topic) : ''}`;
      const res = await fetch(url, { headers });
      const data = await res.json();
      if (data.success) {
        if (data.data.questions.length === 0) {
          setError(topic
            ? `No questions available for ${topic} in Grade ${grade}. Try another topic.`
            : `No questions available for Grade ${grade}.`
          );
          setQuestions([]);
        } else {
          setQuestions(data.data.questions);
          setCurrentIndex(0);
          setShowingAnswer(false);
          setAnswerResult(null);
        }
      }
    } catch (e) {
      setError('Failed to load questions. Please try again.');
    }
    setLoading(false);
  };

  // Submit answer
  const handleAnswer = async (answer, hintsUsed, timeSpent, isNext = false) => {
    if (isNext) {
      // Move to next question
      if (currentIndex + 1 < questions.length) {
        setCurrentIndex(prev => prev + 1);
        setShowingAnswer(false);
        setAnswerResult(null);
      } else {
        // Batch complete — fetch more
        await fetchQuestions();
        await fetchStats();
      }
      return;
    }

    const question = questions[currentIndex];
    const correctAnswer = question.options
      ? question.options.find(o => o.isCorrect)?.label
      : null;
    const isCorrect = correctAnswer
      ? answer === correctAnswer
      : answer.toLowerCase().trim() === (question.correctAnswer || '').toLowerCase().trim();

    try {
      const res = await fetch(`${API_URL}/smart-study/review`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          questionId: question._id,
          isCorrect,
          userAnswer: answer,
          attempts: 1,
          hintsUsed,
          skipped: false,
          timeSpent
        })
      });
      const data = await res.json();
      if (data.success) {
        setAnswerResult(data.data);
        setShowingAnswer(true);
        setSessionStats(prev => ({
          answered: prev.answered + 1,
          correct: prev.correct + (isCorrect ? 1 : 0)
        }));
      }
    } catch (e) {
      console.error('Failed to submit review:', e);
    }
  };

  // Skip question
  const handleSkip = async () => {
    const question = questions[currentIndex];
    try {
      const res = await fetch(`${API_URL}/smart-study/review`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          questionId: question._id,
          isCorrect: false,
          userAnswer: '',
          attempts: 1,
          hintsUsed: 0,
          skipped: true,
          timeSpent: 0
        })
      });
      const data = await res.json();
      if (data.success) {
        setAnswerResult({ ...data.data, skipped: true });
        setShowingAnswer(true);
        setSessionStats(prev => ({ ...prev, answered: prev.answered + 1 }));
      }
    } catch (e) {
      // Move on even if server fails
      if (currentIndex + 1 < questions.length) {
        setCurrentIndex(prev => prev + 1);
      } else {
        await fetchQuestions();
      }
    }
  };

  // Reset progress
  const handleReset = async () => {
    try {
      await fetch(`${API_URL}/smart-study/reset`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ grade, topic: topic || undefined })
      });
      setResetDialog(false);
      await fetchStats();
      setQuestions([]);
      setSessionStats({ answered: 0, correct: 0 });
    } catch (e) {
      console.error('Failed to reset:', e);
    }
  };

  useEffect(() => { fetchTopics(); }, [fetchTopics]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const currentQuestion = questions[currentIndex];

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Brain size={28} color="#6C63FF" />
        <Box>
          <Typography variant="h5" fontWeight={700}>Smart Study</Typography>
          <Typography variant="body2" color="text.secondary">
            SM-2 spaced repetition — questions adapt to your performance
          </Typography>
        </Box>
      </Box>

      {/* Grade + Topic selectors */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Grade</InputLabel>
          <Select
            value={grade}
            label="Grade"
            onChange={e => { setGrade(e.target.value); setTopic(''); setQuestions([]); }}
          >
            {[8, 9, 10, 11, 12].map(g => (
              <MenuItem key={g} value={g}>Grade {g}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
          <InputLabel>Topic (optional)</InputLabel>
          <Select
            value={topic}
            label="Topic (optional)"
            onChange={e => { setTopic(e.target.value); setQuestions([]); }}
          >
            <MenuItem value="">All topics</MenuItem>
            {topics.map(t => (
              <MenuItem key={t.topic} value={t.topic}>
                {t.topic} ({t.questionCount})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          onClick={fetchQuestions}
          startIcon={<Zap size={16} />}
          disabled={loading}
          sx={{ textTransform: 'none', px: 3 }}
        >
          {questions.length > 0 ? 'New Batch' : 'Start Studying'}
        </Button>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 2, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Tab label="Study" sx={{ textTransform: 'none' }} />
        <Tab
          label={`Stats${stats?.dueToday ? ` (${stats.dueToday} due)` : ''}`}
          sx={{ textTransform: 'none' }}
        />
      </Tabs>

      {/* Tab 0: Study */}
      {tab === 0 && (
        <Box>
          {/* Session stats bar */}
          {sessionStats.answered > 0 && (
            <Paper
              elevation={0}
              sx={{
                display: 'flex', gap: 3, p: 1.5, mb: 2,
                bgcolor: 'action.hover', borderRadius: 2, justifyContent: 'center'
              }}
            >
              <Typography variant="body2">
                Answered: <strong>{sessionStats.answered}</strong>
              </Typography>
              <Typography variant="body2">
                Correct: <strong>{sessionStats.correct}</strong>
              </Typography>
              <Typography variant="body2">
                Accuracy:{' '}
                <strong>
                  {sessionStats.answered > 0
                    ? Math.round((sessionStats.correct / sessionStats.answered) * 100)
                    : 0}%
                </strong>
              </Typography>
            </Paper>
          )}

          {loading && (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <CircularProgress size={40} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Loading questions...
              </Typography>
            </Box>
          )}

          {error && (
            <Alert severity="info" sx={{ mb: 2 }} icon={<AlertCircle size={18} />}>
              {error}
            </Alert>
          )}

          {!loading && !error && questions.length === 0 && (
            <Paper
              elevation={0}
              sx={{
                p: 6, textAlign: 'center',
                border: '1px dashed', borderColor: 'divider', borderRadius: 3
              }}
            >
              <Brain size={48} color="#ccc" />
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Ready to study?</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Select a grade, optionally a topic, and hit "Start Studying".
                Questions you struggle with will appear more often — mastered ones space out.
              </Typography>
            </Paper>
          )}

          {!loading && currentQuestion && (
            <QuestionCard
              question={currentQuestion}
              onAnswer={handleAnswer}
              onSkip={handleSkip}
              showingAnswer={showingAnswer}
              answerResult={answerResult}
              currentIndex={currentIndex}
              totalCount={questions.length}
            />
          )}
        </Box>
      )}

      {/* Tab 1: Stats */}
      {tab === 1 && (
        <Box>
          <StatsOverview stats={stats} loading={statsLoading} />

          {stats && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {stats.totalEngaged} of {stats.totalQuestionsAvailable} questions engaged
                ({stats.percentageEngaged}%)
              </Typography>
              <LinearProgress
                variant="determinate"
                value={stats.percentageEngaged}
                sx={{ height: 8, borderRadius: 4, mt: 1 }}
              />
            </Box>
          )}

          <TopicProgress topics={stats?.topicBreakdown} />

          {/* Recent activity */}
          {stats?.recentActivity && stats.recentActivity.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Last 7 Days
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {stats.recentActivity.map(day => (
                  <Tooltip key={day._id} title={`${day.reviewed} reviewed, avg quality ${(day.avgQuality || 0).toFixed(1)}`}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1, textAlign: 'center', minWidth: 60,
                        border: '1px solid', borderColor: 'divider', borderRadius: 1
                      }}
                    >
                      <Typography variant="caption" display="block">
                        {new Date(day._id).toLocaleDateString('en-ZA', { weekday: 'short' })}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>{day.reviewed}</Typography>
                    </Paper>
                  </Tooltip>
                ))}
              </Box>
            </Box>
          )}

          {/* Reset button */}
          <Divider sx={{ my: 3 }} />
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<RotateCcw size={14} />}
            onClick={() => setResetDialog(true)}
            sx={{ textTransform: 'none' }}
          >
            Reset Progress{topic ? ` for ${topic}` : ` for Grade ${grade}`}
          </Button>

          <Dialog open={resetDialog} onClose={() => setResetDialog(false)}>
            <DialogTitle>Reset SM-2 Progress?</DialogTitle>
            <DialogContent>
              <Typography variant="body2">
                This will clear all spaced repetition data
                {topic ? ` for ${topic}` : ` for Grade ${grade}`}.
                All questions will be treated as new. This cannot be undone.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Your answers in the Metrics feed (Progress) are NOT affected.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setResetDialog(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
              <Button
                onClick={handleReset}
                color="error"
                variant="contained"
                sx={{ textTransform: 'none' }}
              >
                Reset
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}
    </Box>
  );
}

// ---- Exported component with feature gate ----

export default function SmartStudy() {
  return (
    <FeatureGate
      feature="spaced_repetition"
      fallback={<UpgradePrompt feature="spaced_repetition" />}
    >
      <SmartStudyContent />
    </FeatureGate>
  );
}
